// Seasonal particles: falling snow, birch leaves, confetti and hearts, Halloween bats and bonfire
// sparks. Each layer is one Points draw in a box that wraps around the camera (or a fixed spot),
// so they cost almost nothing. Shapes are drawn in the fragment shader.
import { AdditiveBlending, BufferAttribute, BufferGeometry, NormalBlending, Points, ShaderMaterial, Uniform, Vector3, type Camera } from 'three';
import type { Rgb } from './daylight';
import { rng } from './layout';

export type ParticleKind = 'snow' | 'leaves' | 'confetti' | 'hearts' | 'bats' | 'sparks';

interface KindSettings {
  count: number;
  /** Wrapping box around the centre, metres. */
  box: [number, number, number];
  /** Fall speed (m/s; negative rises), sideways sway (m) and size (m). */
  fall: number; sway: number; size: number;
  colours: Rgb[];
  additive?: boolean;
  /** Lit by the scene's light (`setLight`), or glowing on its own. */
  lit: boolean;
}

const KINDS: Record<ParticleKind, KindSettings> = {
  snow: { count: 6000, box: [60, 26, 60], fall: 0.9, sway: 0.5, size: 0.045, colours: [[0.95, 0.97, 1.0]], lit: true },
  leaves: { count: 700, box: [50, 18, 50], fall: 1.1, sway: 1.4, size: 0.09, lit: true,
    colours: [[0.95, 0.6, 0.08], [0.85, 0.36, 0.06], [0.98, 0.78, 0.2], [0.55, 0.32, 0.1]] },
  confetti: { count: 1600, box: [44, 18, 44], fall: 0.8, sway: 0.7, size: 0.06, lit: true,
    colours: [[1.0, 0.25, 0.55], [1.0, 0.82, 0.2], [0.35, 0.6, 1.0], [0.4, 0.9, 0.45]] },
  hearts: { count: 700, box: [44, 18, 44], fall: 0.45, sway: 0.8, size: 0.14, lit: true,
    colours: [[1.0, 0.18, 0.35], [1.0, 0.45, 0.65], [0.9, 0.08, 0.2]] },
  bats: { count: 40, box: [120, 22, 120], fall: 0, sway: 0, size: 0.9, lit: true, colours: [[0.05, 0.04, 0.06]] },
  sparks: { count: 500, box: [6, 16, 6], fall: -2.2, sway: 0.6, size: 0.07, lit: false, additive: true,
    colours: [[6.0, 2.4, 0.5], [5.0, 1.6, 0.3]] },
};

const SHAPES: Record<ParticleKind, number> = { snow: 0, leaves: 1, confetti: 2, hearts: 3, bats: 4, sparks: 0 };

export class Particles {
  readonly points: Points<BufferGeometry, ShaderMaterial>;
  private uniforms = {
    uTime: new Uniform(0),
    uCenter: new Uniform(new Vector3()),
    uAmount: new Uniform(1),
    uScale: new Uniform(600),
    uLight: new Uniform(new Vector3(1, 1, 1)),
  };
  private anchor: Vector3 | null;

  /** `palette` replaces the kind's colours (up to four); `anchor` fixes the box at a spot instead of the camera. */
  constructor(readonly kind: ParticleKind, palette?: Rgb[], anchor?: Vector3) {
    const k = KINDS[kind];
    this.anchor = anchor ?? null;
    const colours = (palette ?? k.colours).slice(0, 4);
    while (colours.length < 4) colours.push(colours[colours.length % Math.max(1, colours.length)]!);
    const r = rng(kind.length * 977 + 13);
    const pos = new Float32Array(k.count * 3), rnd = new Float32Array(k.count * 4);
    for (let i = 0; i < pos.length; i++) pos[i] = r();
    for (let i = 0; i < rnd.length; i++) rnd[i] = r();
    const g = new BufferGeometry();
    g.setAttribute('position', new BufferAttribute(pos, 3));
    g.setAttribute('aRnd', new BufferAttribute(rnd, 4));
    const v3 = (c: Rgb) => `vec3(${c.map((x) => x.toFixed(3)).join(', ')})`;
    const mat = new ShaderMaterial({
      uniforms: this.uniforms,
      transparent: true, depthWrite: false,
      blending: k.additive ? AdditiveBlending : NormalBlending,
      defines: { SHAPE: SHAPES[kind], LIT: k.lit ? 1 : 0, BATS: kind === 'bats' ? 1 : 0, ANCHORED: anchor ? 1 : 0 },
      vertexShader: /* glsl */ `
        attribute vec4 aRnd;
        uniform float uTime, uAmount, uScale;
        uniform vec3 uCenter;
        varying vec3 vColour;
        varying float vA, vRot, vFlip;
        const vec3 BOX = vec3(${k.box.map((x) => x.toFixed(1)).join(', ')});
        void main() {
          float t = uTime;
          vec3 local = position * BOX;
          vec3 wp;
        #if BATS
          // Bats circle in loose loops over the stadium, ahead of the camera.
          float a = t * (0.25 + aRnd.x * 0.35) * (aRnd.y > 0.5 ? 1.0 : -1.0) + aRnd.z * 6.2831;
          float rad = 8.0 + aRnd.w * 30.0;
          wp = uCenter + vec3((position.x - 0.5) * BOX.x, 0.0, (position.z - 0.5) * BOX.z) * 0.6
             + vec3(cos(a) * rad, 0.0, sin(a * 1.3) * rad * 0.6);
          wp.y = 9.0 + position.y * BOX.y + sin(t * 1.7 + aRnd.z * 9.0) * 1.5;
          vFlip = sin(t * (11.0 + aRnd.x * 6.0) + aRnd.y * 20.0);
        #else
          local.y -= t * ${k.fall.toFixed(2)} * (0.7 + aRnd.x * 0.6);
          local.x += sin(t * (0.6 + aRnd.y) + aRnd.z * 6.2831) * ${k.sway.toFixed(2)};
          local.z += cos(t * (0.5 + aRnd.z * 0.8) + aRnd.w * 6.2831) * ${k.sway.toFixed(2)};
          #if ANCHORED
            // Rising from a spot (a bonfire), spreading as they climb.
            float life = fract(local.y / BOX.y);
            wp = uCenter + vec3((local.x - BOX.x * 0.5) * (0.25 + life), life * BOX.y, (local.z - BOX.z * 0.5) * (0.25 + life));
          #else
            wp = local + BOX * floor((uCenter - local) / BOX + 0.5);
          #endif
          vFlip = cos(t * (2.0 + aRnd.y * 4.0) + aRnd.x * 6.2831);
        #endif
          vRot = aRnd.w * 6.2831 + t * (aRnd.x - 0.5) * 3.0;
          vec4 mv = viewMatrix * vec4(wp, 1.0);
          gl_Position = projectionMatrix * mv;
          float sizePx = ${k.size.toFixed(3)} * (0.7 + aRnd.y * 0.6) * uScale / max(0.1, -mv.z);
          gl_PointSize = clamp(sizePx, 1.0, 64.0);
          // Fade far away, below the ground, and as tiny points get too small to read.
          float dist = length(wp - cameraPosition);
          vA = uAmount * step(0.02, wp.y) * (1.0 - smoothstep(BOX.x * 0.3, BOX.x * 0.5, dist)) * clamp(sizePx, 0.0, 1.0);
        #if ANCHORED
          vA = uAmount * (1.0 - life);
        #endif
          int ci = int(floor(aRnd.z * 3.999));
          vColour = ci == 0 ? ${v3(colours[0]!)} : ci == 1 ? ${v3(colours[1]!)} : ci == 2 ? ${v3(colours[2]!)} : ${v3(colours[3]!)};
        }`,
      fragmentShader: /* glsl */ `
        uniform vec3 uLight;
        varying vec3 vColour;
        varying float vA, vRot, vFlip;
        void main() {
          vec2 p = gl_PointCoord - 0.5;
          float c = cos(vRot), s = sin(vRot);
          vec2 q = vec2(c * p.x - s * p.y, s * p.x + c * p.y);
          float a;
          vec3 col = vColour;
        #if SHAPE == 0
          a = smoothstep(0.5, 0.15, length(p));
        #elif SHAPE == 1
          // A birch leaf: a pointed oval, turning over as it falls.
          q.x /= max(0.15, abs(vFlip));
          float r = length(vec2(q.x * 1.5, q.y));
          a = smoothstep(0.42, 0.36, r + abs(q.x) * 0.25);
          col *= 0.75 + 0.25 * step(0.03, abs(q.x)) + 0.2 * vFlip;
        #elif SHAPE == 2
          // Confetti: a small flipping strip.
          q.x /= max(0.08, abs(vFlip));
          a = step(abs(q.x), 0.18) * step(abs(q.y), 0.42);
          col *= 0.8 + 0.3 * vFlip;
        #elif SHAPE == 3
          // A heart: (x² + y² − 1)³ − x²y³ < 0.
          float hx = q.x * 2.5, hy = -q.y * 2.5 + 0.12;
          float f = pow(hx * hx + hy * hy - 1.0, 3.0) - hx * hx * hy * hy * hy;
          a = smoothstep(0.03, -0.03, f);
          col *= 0.85 + 0.15 * vFlip;
        #else
          // A bat: body and two flapping wings.
          vec2 b = vec2(abs(p.x), -p.y);
          float thick = 0.11 * (1.0 - b.x / 0.46);
          float rel = b.y - 0.6 * vFlip * b.x;
          float wings = step(b.x, 0.46) * step(-thick * 0.5 + 0.03 * abs(sin(b.x * 22.0)), rel) * step(rel, thick);
          float body = step(length(vec2(p.x * 2.4, p.y)), 0.13);
          a = max(wings, body);
        #endif
          a *= vA;
          if (a < 0.02) discard;
        #if LIT
          col *= uLight;
        #endif
          gl_FragColor = vec4(col, a);
        }`,
    });
    this.points = new Points(g, mat);
    this.points.frustumCulled = false;
    this.points.name = `particles-${kind}`;
  }

  /** How much of the layer shows, 0..1. */
  set amount(v: number) {
    this.uniforms.uAmount.value = v;
    this.points.visible = v > 0.01;
  }

  /** The light falling on lit particles (sun, sky and floodlights together). */
  setLight(c: Rgb): void { this.uniforms.uLight.value.set(...c); }

  update(timeS: number, camera: Camera & { fov?: number }, viewportHeight: number): void {
    this.uniforms.uTime.value = timeS;
    this.uniforms.uCenter.value.copy(this.anchor ?? camera.position);
    const fov = ((camera.fov ?? 50) * Math.PI) / 180;
    this.uniforms.uScale.value = viewportHeight / (2 * Math.tan(fov / 2));
  }

  dispose(): void {
    this.points.geometry.dispose();
    this.points.material.dispose();
  }
}
